import React from 'react';
import { clsx } from 'clsx';

// Props para Card
interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

// Props para CardHeader
interface CardHeaderProps {
  title: React.ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

// Props para CardContent
interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

// Props para CardFooter
interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Componente Card seguindo o design system
 */
export const Card: React.FC<CardProps> = ({
  children,
  className,
  hover = false,
  padding = 'md',
  onClick,
}) => {
  const baseClasses = hover ? 'card-hover' : 'card';
  
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  const cardClasses = clsx(
    baseClasses,
    paddingClasses[padding],
    {
      'cursor-pointer': onClick,
    },
    className
  );

  return (
    <div className={cardClasses} onClick={onClick}>
      {children}
    </div>
  );
};

/**
 * Header do Card com título e ação opcional
 */
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className
}) => {
  return (
    <div className={clsx('flex items-center justify-between mb-4', className)}>
      <div>
        <h3 className="text-h4">{title}</h3>
        {subtitle && (
          <p className="text-small text-slate-500 mt-1">{subtitle}</p>
        )}
      </div>
      {action && (
        <div className="flex items-center gap-2">
          {action}
        </div>
      )}
    </div>
  );
};

/**
 * Conteúdo do Card
 */
export const CardContent: React.FC<CardContentProps> = ({
  children,
  className
}) => {
  return (
    <div className={clsx('text-body', className)}>
      {children}
    </div>
  );
};

/**
 * Footer do Card
 */
export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className
}) => {
  return (
    <div className={clsx('mt-4 pt-4 border-t border-slate-200', className)}>
      {children}
    </div>
  );
};

// Props para Badge
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Componente Badge seguindo o design system
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'neutral',
  size = 'md',
  className
}) => {
  const variantClasses = {
    success: 'badge-success',
    warning: 'badge-warning', 
    danger: 'badge-danger',
    neutral: 'badge-neutral',
    info: 'text-info-700 bg-info-100'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm'
  };

  const badgeClasses = clsx(
    'inline-flex items-center font-medium rounded-full',
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  return (
    <span className={badgeClasses}>
      {children}
    </span>
  );
};

// Badges especializados para status comuns
export const StatusBadge: React.FC<{ 
  status: 'ativo' | 'inativo' | 'publicado' | 'rascunho' | 'em-estoque' | 'sem-estoque' | 'estoque-baixo';
  className?: string;
}> = ({ status, className }) => {
  const statusConfig = {
    'ativo': { variant: 'success' as const, text: 'Ativo' },
    'inativo': { variant: 'neutral' as const, text: 'Inativo' },
    'publicado': { variant: 'success' as const, text: 'Publicado' },
    'rascunho': { variant: 'neutral' as const, text: 'Rascunho' },
    'em-estoque': { variant: 'success' as const, text: 'Em Estoque' },
    'sem-estoque': { variant: 'danger' as const, text: 'Sem Estoque' },
    'estoque-baixo': { variant: 'warning' as const, text: 'Estoque Baixo' },
  };

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} className={className}>
      {config.text}
    </Badge>
  );
};

// Props para Skeleton
interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  lines?: number;
  variant?: 'text' | 'rectangular' | 'circular';
}

/**
 * Componente Skeleton para loading states
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  width,
  height,
  lines = 1,
  variant = 'text'
}) => {
  const baseClasses = 'animate-pulse bg-slate-200';
  
  const variantClasses = {
    text: 'rounded h-4',
    rectangular: 'rounded',
    circular: 'rounded-full'
  };

  const style: React.CSSProperties = {};
  if (width) style.width = width;
  if (height) style.height = height;

  if (lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={clsx(
              baseClasses,
              variantClasses[variant],
              className
            )}
            style={style}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        baseClasses,
        variantClasses[variant],
        className
      )}
      style={style}
    />
  );
};